import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import { useAppDispatch, useAppSelector } from './store';
import { getCurrentUser, logout } from './store/slices/authSlice';
import { ActivityTracker } from './common/utils/activityTracker';
import { sessionStorageUtils } from './common/utils/sessionStorage';
import { useTokenInterceptor } from './hooks/useTokenInterceptor';
// Auth config removed; using inline inactivity timeout

// Components
import MasterLayout from './common/components/MasterLayout';
import ToastHost from './common/components/ToastHost';
import { Loader } from './common/components';
import { ProtectedRoute, Login, VanchiStatus, Report, About, Legal, Copyright, Contact, AddUserPage, AddNada, AddVanchiName, AddVanchiNo } from './pages';
import UsersGrid from './pages/AddUser/grid';

// Theme is imported in main.tsx

// App Routes Component
const AppRoutes: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, isLoading } = useAppSelector((state) => state.auth);
  const activityTrackerRef = useRef<ActivityTracker | null>(null);
  
  // Initialize token interceptor for automatic logout
  useTokenInterceptor();

  // Inactivity timeout (minutes) - configurable here
  const INACTIVITY_TIMEOUT_MINUTES = 10;

  useEffect(() => {
    // Check if user is already authenticated
    if (sessionStorageUtils.getToken()) {
      dispatch(getCurrentUser());
    }
  }, [dispatch]);

  useEffect(() => {
    // Initialize activity tracker when user is authenticated
    if (isAuthenticated && !activityTrackerRef.current) {
      activityTrackerRef.current = new ActivityTracker({
        timeoutMinutes: INACTIVITY_TIMEOUT_MINUTES,
        onTimeout: () => {
          dispatch(logout());
          sessionStorageUtils.clear();
        }
      });
      activityTrackerRef.current.start();
    }

    // Cleanup activity tracker when user logs out
    if (!isAuthenticated && activityTrackerRef.current) {
      activityTrackerRef.current.stop();
      activityTrackerRef.current = null;
    }

    // Cleanup on unmount
    return () => {
      if (activityTrackerRef.current) {
        activityTrackerRef.current.stop();
      }
    };
  }, [isAuthenticated, dispatch]);

  const handleLogout = () => {
    dispatch(logout());
    sessionStorageUtils.clear();
  };


  // Show loader while checking authentication
  if (isLoading) {
    return <Loader fullScreen text="Loading..." />;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <MasterLayout onLogout={handleLogout}>
      <ToastHost />
      <Routes>
        <Route path="/vanchistatus" element={<VanchiStatus />} />
        <Route 
          path="/users" 
          element={
            <ProtectedRoute>
              <UsersGrid />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/report" 
          element={
            <ProtectedRoute>
              <Report />
            </ProtectedRoute>
          } 
        />
        <Route path="/about" element={<About />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/copyright" element={<Copyright />} />
        <Route path="/contact" element={<Contact />} />
        
        {/* Master Records Routes */}
        <Route 
          path="/AddUser" 
          element={
            <ProtectedRoute>
              <AddUserPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/AddNada" 
          element={
            <ProtectedRoute>
              <AddNada />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/AddVanchiName" 
          element={
            <ProtectedRoute>
              <AddVanchiName />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/AddVanchiNo" 
          element={
            <ProtectedRoute>
              <AddVanchiNo />
            </ProtectedRoute>
          } 
        />
        
        <Route path="/" element={<Navigate to="/vanchistatus" replace />} />
      </Routes>
    </MasterLayout>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <Provider store={store}>
      <Router>
        <AppRoutes />
      </Router>
    </Provider>
  );
};

export default App;
